import { useQuery, useMutation } from "@tanstack/react-query";
import queryString from "query-string";

import { api } from "@services/index";
import {
  ApiErrorModel,
  SubmissionModel,
  SubmissionModelDto,
  PaginationModelDto,
  PaginationModel,
  QuestionnaireModelDto,
  QuestionnaireModel,
  FileModel,
} from "@interfaces/index";

const prepareSubmissionToModel = (
  submission: SubmissionModelDto,
): SubmissionModel => ({
  uniqueId: submission.unique_id,
  period: {
    title: submission.period.title,
    status: {
      value: submission.period.status.value,
      label: submission.period.status.label,
    },
    startDate: submission.period.start_date,
    endDate: submission.period.end_date,
  },
  userBusiness: {
    uniqueId: submission.user_business.unique_id,
    name: submission.user_business.name,
    type: submission.user_business.type,
    tradingStartDate: submission.user_business.trading_start_date,
  },
  status: {
    label: submission.status.label,
    value: submission.status.value,
  },
  comment: submission.comment,
  income: String(submission.income),
  business: String(submission.business),
  private: String(submission.private),
  expense: String(submission.expense),
  profit: String(submission.profit),
  policies: {
    canUpdate: submission.policies.canUpdate,
  },
});

export const useSubmissionGetAll = ({
  businessId = "",
  status = "",
  startDate = "",
  endDate = "",
  page,
}: {
  businessId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
}) => {
  const queryFn = async () => {
    const params = queryString.stringify(
      {
        user_business_unique_id: businessId,
        status,
        start_date: startDate ? `${startDate}T00:00:00` : "",
        end_date: endDate ? `${endDate}T23:59:59` : "",
        page,
      },
      { skipEmptyString: true },
    );
    const { data: dataDto } = await api.get<{
      data: SubmissionModelDto[];
      meta: PaginationModelDto;
    }>(`/submissions?${params}`);

    const submissions = dataDto.data.map((submission) =>
      prepareSubmissionToModel(submission),
    );
    const pagination: PaginationModel = {
      total: dataDto.meta.total,
      perPage: dataDto.meta.per_page,
    };

    return {
      submissions,
      pagination,
    };
  };

  return useQuery<
    {
      submissions: SubmissionModel[];
      pagination: PaginationModel;
    },
    ApiErrorModel
  >({
    queryKey: [
      "submissionGetAll",
      businessId,
      status,
      startDate,
      endDate,
      page,
    ],
    queryFn,
    refetchOnWindowFocus: true,
    enabled: !!businessId,
  });
};

export const useSubmissionGetOne = (id: string) => {
  const queryFn = async () => {
    const { data: dataDto } = await api.get<SubmissionModelDto>(
      `/submissions/${id}`,
    );

    return prepareSubmissionToModel(dataDto);
  };

  return useQuery<SubmissionModel, ApiErrorModel>({
    queryKey: ["submissionGetOne", id],
    queryFn,
    enabled: !!id,
  });
};

export const useSubmissionSubmitData = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    submissionId,
    comment,
  }: {
    submissionId: string;
    comment: string;
  }) => {
    const fieldsDto = { comment };
    await api.post(`/submissions/${submissionId}/submit`, fieldsDto);
  };

  return useMutation({
    mutationKey: ["submissionSubmitData"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      if (onError) {
        onError({ ...error });
      }
    },
  });
};

export const useQuestionnaireGetObj = (submissionId: string) => {
  const queryFn = async () => {
    const { data: dataDto } = await api.get<QuestionnaireModelDto>(
      `/submissions/${submissionId}/questionnaire`,
    );

    return {
      linked: dataDto.linked,
      name: dataDto.name,
      status: dataDto.status,
      statusLabel: dataDto.status_label,
      questions: !dataDto.questions?.length
        ? []
        : dataDto.questions?.map((question) => ({
            id: question.id,
            uniqueId: question.unique_id,
            order: question.order,
            title: question.title,
            subtitle: question.subtitle,
            documentNote: question.document_note,
            attachmentRequirement: question.attachment_requirement,
            answer: question.answer,
            answerUpdated: question.answer_updated_at,
            attachments: !question.attachments?.length
              ? []
              : question.attachments.map((attachment) => ({
                  uniqueId: attachment.unique_id,
                  originalName: attachment.original_name,
                  url: attachment.url,
                  size: attachment.size,
                })),
            documentMissing: question.document_missing,
          })),
    };
  };

  return useQuery<QuestionnaireModel, ApiErrorModel>({
    queryKey: ["questionnaireGetObj", submissionId],
    queryFn,
    enabled: !!submissionId,
  });
};

export const useQuestionnaireUpdateAnswer = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    submissionId,
    questionId,
    answerText,
  }: {
    submissionId: string;
    questionId: string;
    answerText: string;
  }) => {
    const fieldsDto = { answer_text: answerText };
    await api.patch(
      `/submissions/${submissionId}/questionnaire/answers/${questionId}`,
      fieldsDto,
    );
  };

  return useMutation({
    mutationKey: ["questionnaireUpdateAnswer"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      if (onError) {
        onError({ ...error });
      }
    },
  });
};

export const useQuestionnaireAppendFiles = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    submissionId,
    questionId,
    files,
  }: {
    submissionId: string;
    questionId: string;
    files: FileModel[];
  }) => {
    const fieldsDto = {
      files: files?.map((file) => ({
        key: file.key,
        uuid: file.uuid,
        original_name: file.originalName,
      })),
    };
    await api.post(
      `/submissions/${submissionId}/questionnaire/answers/${questionId}/attachments`,
      fieldsDto,
    );
  };

  return useMutation({
    mutationKey: ["questionnaireAppendFiles"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      if (onError) {
        onError({ ...error });
      }
    },
  });
};

export const useQuestionnaireDeleteFile = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    submissionId,
    questionId,
    fileId,
  }: {
    submissionId: string;
    questionId: string;
    fileId: string;
  }) =>
    await api.delete(
      `/submissions/${submissionId}/questionnaire/answers/${questionId}/attachments/${fileId}`,
    );

  return useMutation({
    mutationKey: ["questionnaireDeleteFile"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      if (onError) {
        onError({ ...error });
      }
    },
  });
};

export const useQuestionnaireSubmit = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({ submissionId }: { submissionId: string }) =>
    await api.post(`/submissions/${submissionId}/questionnaire/submit`);

  return useMutation({
    mutationKey: ["questionnaireSubmit"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      if (onError) {
        onError({ ...error });
      }
    },
  });
};
